// Builds a self-contained, parameterized Redshift query for the compare-to-canonical
// (BPM) layer folded into Portfolio/Channels. Embeds the model's own MMM dataframe
// values (spend/impressions already loaded client-side) as a literal table (a `union
// all select` chain, not a `values (...) as t(...)` derived table -- confirmed against
// real Redshift that the latter throws a syntax error partway through a multi-row
// list; `union all select` is the standard, reliably-supported way to inline literal
// rows in Redshift), live-joins the canonical BPM aggregate for the same weeks, and
// returns only weeks whose deviation exceeds a threshold the caller fills in
// themselves via DataGrip's ${paramName} placeholder convention (never <angle-bracket>
// text, which DataGrip doesn't recognize and Redshift would silently compare against
// as a literal string).

export interface GroundTruthSqlWeek {
  weekIso: string;
  mmmSpend: number;
  mmmImpressions: number;
  // Already-loaded BPM ground truth for the same week (WeekRow.bpmSpend/bpmImpressions)
  // -- only used for the always-visible aggregate deviation readout below, not the SQL
  // itself (that re-derives BPM live from Redshift so it reflects the database at query
  // time, not whatever was true when the page loaded).
  bpmSpend?: number;
  bpmImpressions?: number;
  // Per-platform breakdown of this week's MMM spend/impressions (see WeekRow.perChannel's
  // own perPlatform doc comment), keyed by platform key -- when the caller also passes
  // `platforms` to buildGroundTruthSql, the query joins mmm/bpm by (week, platform)
  // instead of just week, so it can isolate which platform actually diverges rather than
  // only ever comparing the channel-level total (Sean, 2026-09-02).
  perPlatform?: Record<string, { spend: number; impressions: number }>;
}

export interface AggregateDeviation {
  spendPct: number;
  impressionsPct: number;
}

// Portfolio-/channel-wide deviation across the training window: sum every week's MMM
// and BPM values first, then compare the totals (MMM / BPM - 1) -- not an average of
// per-week ratios, which would let a handful of tiny, noisy weeks dominate the number.
// Returns undefined when there's no BPM total to divide by (e.g. every week's bpmSpend
// is 0/undefined).
//
// Spend and impressions coverage are independent (a week can have bpmSpend but not
// bpmImpressions, or vice versa -- see this interface's own doc comment), so each
// metric's MMM sum only includes weeks where that SAME metric's BPM value is defined,
// rather than summing MMM over every week regardless. Summing mmmSpend unconditionally
// while `?? 0`-ing bpmSpend made the numerator cover more weeks than the denominator
// whenever BPM ground truth had a coverage gap (common), skewing the ratio purely from
// the gap, not a real mismatch.
export const computeAggregateDeviation = (
  weeks: GroundTruthSqlWeek[]
): AggregateDeviation | undefined => {
  const totals = weeks.reduce(
    (acc, w) => ({
      mmmSpend: acc.mmmSpend + (w.bpmSpend !== undefined ? w.mmmSpend : 0),
      bpmSpend: acc.bpmSpend + (w.bpmSpend ?? 0),
      mmmImpressions: acc.mmmImpressions + (w.bpmImpressions !== undefined ? w.mmmImpressions : 0),
      bpmImpressions: acc.bpmImpressions + (w.bpmImpressions ?? 0)
    }),
    { mmmSpend: 0, bpmSpend: 0, mmmImpressions: 0, bpmImpressions: 0 }
  );
  if (totals.bpmSpend === 0 && totals.bpmImpressions === 0) {
    return undefined;
  }
  return {
    spendPct: totals.bpmSpend > 0 ? totals.mmmSpend / totals.bpmSpend - 1 : 0,
    impressionsPct:
      totals.bpmImpressions > 0 ? totals.mmmImpressions / totals.bpmImpressions - 1 : 0
  };
};

// Same convention as data-qa-utils.ts's bucketEndDate: 'weekly' -> week, 'monthly' ->
// month, anything else (daily/unrecognized) -> day. Determines which date_trunc unit
// the canonical (BPM) side buckets by -- it MUST match weeks[].weekIso's own bucket
// grain (bucketWeeksByGranularity, backend), or the join below silently either
// compares mismatched grains (a week against a month) or drops every row (a Monday
// join key that a monthly bucket's 1st-of-month weekIso will essentially never hit).
const dateTruncUnitFor = (reviewGranularity: string | undefined): 'week' | 'month' | 'day' => {
  const granularity = (reviewGranularity ?? '').trim().toLowerCase();
  if (granularity === 'weekly') return 'week';
  if (granularity === 'monthly') return 'month';
  return 'day';
};

interface BuildGroundTruthSqlArgs {
  company: string;
  // Should already be scoped to the model's training window (fencepost) -- weeks
  // outside it aren't part of what the model trains on, so flagging them here would
  // misrepresent what a deviation actually means.
  weeks: GroundTruthSqlWeek[];
  // Title Case 'Mapped Channel' value (e.g. 'Paid Search') to scope to one channel's
  // card. Omitted for the Portfolio-wide total across every channel.
  channelLabel?: string;
  // dataset.reviewGranularity -- see dateTruncUnitFor above. Defaults to weekly (the
  // pre-fix, and still by far the most common, bucketing) when omitted, rather than
  // silently degrading to daily for an existing caller that hasn't been updated yet.
  reviewGranularity?: string;
  // Platforms to break the query out by, keyed the same way weeks[].perPlatform is --
  // omitted for the Portfolio-wide total or a single-platform channel, where a platform
  // dimension would either be meaningless (no one channel) or redundant (identical to
  // the channel itself). Like channelLabel, each platform's `label` must already match
  // Mapped Platform's raw Title Case value -- ChannelConfig.platforms derives it the
  // same way buildChannels titles the channel, so this is the same convention, not a
  // new risk.
  platforms?: { key: string; label: string }[];
  // Raw feature_id(s) (e.g. "FEATID2474") to additionally filter the BPM CTE by --
  // needed ONLY for a channel that buildChannels split by demand group (e.g. Paid
  // Search: brand vs non-brand). Redshift's feature_mappings JSON has no demand-group
  // column of its own to filter by (that's a model-config declaration, not a taxonomy
  // dimension Redshift knows about), so channelLabel alone would pull the FULL combined
  // BPM total for both split cards. Omitted for a channel that never splits, where
  // channelLabel alone is already exact.
  featureIds?: string[];
}

export const buildGroundTruthSql = ({
  company,
  weeks,
  channelLabel,
  reviewGranularity = 'weekly',
  platforms,
  featureIds
}: BuildGroundTruthSqlArgs): string => {
  if (weeks.length === 0) {
    return "-- No weeks in the model's training window yet.";
  }
  // `company` is interpolated into a TABLE IDENTIFIER below, not a quoted string
  // literal (channelLabel's own `.replace(/'/g, "''")` escaping doesn't apply to an
  // identifier position) -- a company id containing a hyphen, space, or quote would
  // make Redshift parse the FROM clause as something other than one table name, or
  // fail outright. Real company ids are lowercase snake_case; reject anything else
  // rather than emit SQL that silently means something different from what it looks
  // like.
  if (!/^[a-z0-9_]+$/i.test(company)) {
    return `-- Unsupported company identifier "${company}" for this query.`;
  }

  const truncUnit = dateTruncUnitFor(reviewGranularity);
  const byPlatform = Boolean(platforms && platforms.length > 0);
  const mmmRows = byPlatform
    ? weeks
        .flatMap(w =>
          (platforms ?? []).map(p => {
            const v = w.perPlatform?.[p.key] ?? { spend: 0, impressions: 0 };
            return `  select '${w.weekIso}' as week, '${p.label.replace(/'/g, "''")}' as platform, ${v.spend.toFixed(2)} as mmm_spend, ${Math.round(v.impressions)} as mmm_impressions`;
          })
        )
        .join('\n  union all\n')
    : weeks
        .map(
          w =>
            `  select '${w.weekIso}' as week, ${w.mmmSpend.toFixed(2)} as mmm_spend, ${Math.round(w.mmmImpressions)} as mmm_impressions`
        )
        .join('\n  union all\n');
  const channelFilter = channelLabel
    ? `\n    and json_extract_path_text(feature_mappings, 'Mapped Channel') = '${channelLabel.replace(/'/g, "''")}'`
    : '';
  const featureIdFilter =
    featureIds && featureIds.length > 0
      ? `\n    and feature_id in (${featureIds.map(id => `'${id.replace(/'/g, "''")}'`).join(', ')})`
      : '';
  const platformColumn = byPlatform
    ? "\n    json_extract_path_text(feature_mappings, 'Mapped Platform') as platform,"
    : '';
  const joinCondition = byPlatform
    ? 'b.week = m.week and b.platform = m.platform'
    : 'b.week = m.week';
  const selectColumns = byPlatform ? '  m.week, m.platform,' : '  m.week,';
  const orderColumns = byPlatform ? 'm.week, m.platform' : 'm.week';

  return [
    'with mmm as (',
    mmmRows,
    '),',
    'bpm as (',
    '  select',
    `    to_char(date_trunc('${truncUnit}', date), 'YYYY-MM-DD') as week,${platformColumn}`,
    '    sum(spend) as bpm_spend,',
    '    sum(impressions) as bpm_impressions',
    `  from redshift_lambdas_db.public.mmm_feature_mapped_media_rows__${company}__national`,
    `  where feature_id is not null and feature_id != ''${channelFilter}${featureIdFilter}`,
    `  group by ${byPlatform ? '1, 2' : '1'}`,
    ')',
    'select',
    selectColumns,
    '  m.mmm_spend, b.bpm_spend, round(m.mmm_spend / nullif(b.bpm_spend, 0) - 1, 4) as spend_deviation,',
    '  m.mmm_impressions, b.bpm_impressions, round(m.mmm_impressions / nullif(b.bpm_impressions, 0) - 1, 4) as impressions_deviation',
    'from mmm m',
    `join bpm b on ${joinCondition}`,
    // Literal DataGrip ${paramName} placeholder text for whoever runs this query to
    // fill in (e.g. 1 for 1%), not a JS interpolation.
    // eslint-disable-next-line no-template-curly-in-string
    'where abs(m.mmm_spend / nullif(b.bpm_spend, 0) - 1) > ${threshold_pct} / 100.0',
    // eslint-disable-next-line no-template-curly-in-string
    '   or abs(m.mmm_impressions / nullif(b.bpm_impressions, 0) - 1) > ${threshold_pct} / 100.0',
    `order by ${orderColumns};`
  ].join('\n');
};
